import { useEffect, useState } from "react";

const THEME_KEY = "coreui-theme";
const THEME_CHANGE_EVENT = "theme-change";
type ThemeValue = "light" | "dark";

// Helper to dispatch theme change event for same-tab sync
const dispatchThemeChange = (theme: ThemeValue) => {
  window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: theme }));
};

// Helper to get system preference
const getSystemPreference = (): boolean => {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

// Helper to get stored override
const getStoredOverride = (): ThemeValue | null => {
  const stored = localStorage.getItem(THEME_KEY);
  if (stored === "light" || stored === "dark") {
    return stored;
  }
  return null;
};

// Helper to determine current theme
const resolveTheme = (): boolean => {
  const override = getStoredOverride();
  if (override !== null) {
    return override === "dark";
  }
  return getSystemPreference();
};

export const useDarkMode = () => {
  const [isDark, setIsDark] = useState(resolveTheme);

  // Apply theme to both CoreUI and Bootstrap data attributes
  useEffect(() => {
    const theme = isDark ? "dark" : "light";
    document.documentElement.setAttribute("data-coreui-theme", theme);
    document.documentElement.setAttribute("data-bs-theme", theme);
  }, [isDark]);

  // Listen for system theme changes (only when no override is set)
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      // Only update if no override is set
      if (getStoredOverride() === null) {
        setIsDark(e.matches);
      }
    };

    mediaQuery.addEventListener("change", handleSystemThemeChange);
    return () => mediaQuery.removeEventListener("change", handleSystemThemeChange);
  }, []);

  // Listen for theme changes (same tab via custom event, cross-tab via storage event)
  useEffect(() => {
    // Custom event for same-tab sync
    const handleThemeChange = (e: Event) => {
      const customEvent = e as CustomEvent<ThemeValue>;
      setIsDark(customEvent.detail === "dark");
    };

    // Storage event for cross-tab sync
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === THEME_KEY) {
        if (e.newValue === null) {
          // Override was cleared, follow system preference
          setIsDark(getSystemPreference());
        } else if (e.newValue === "light" || e.newValue === "dark") {
          setIsDark(e.newValue === "dark");
        }
      }
    };

    window.addEventListener(THEME_CHANGE_EVENT, handleThemeChange);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const setLight = () => {
    localStorage.setItem(THEME_KEY, "light");
    dispatchThemeChange("light");
    setIsDark(false);
  };

  const setDark = () => {
    localStorage.setItem(THEME_KEY, "dark");
    dispatchThemeChange("dark");
    setIsDark(true);
  };

  const toggleTheme = () => {
    if (isDark) {
      setLight();
    } else {
      setDark();
    }
  };

  const clearOverride = () => {
    localStorage.removeItem(THEME_KEY);
    setIsDark(getSystemPreference());
  };

  return {
    isDark,
    toggleTheme,
    setLight,
    setDark,
    clearOverride,
  };
};
